/**
 * Pure helpers for ScopeScript object-literal strings in Control Designer.
 * ScopeScript is stored as "({ ... })" — members are object methods/properties.
 */

/**
 * @param {string} controlName
 * @param {string} suffix
 * @returns {string}
 */
export function buildHandlerName(controlName, suffix) {
    return `${controlName}${suffix || ''}`;
}

/**
 * @param {string} handlerName
 * @returns {string}
 */
export function buildMethodCall(handlerName) {
    return `Control.${handlerName}()`;
}

/**
 * Extract handler member name from ActionData.Method.
 * Accepts the stored function name or legacy Control.button1_Click() syntax.
 * @param {string} methodText
 * @returns {string|null}
 */
export function parseMethodCall(methodText) {
    if (!methodText || typeof methodText !== 'string')
        return null;

    const trimmed = methodText.trim();
    const legacyMatch = /^Control\.(\w+)\s*\(\s*\)$/.exec(trimmed);
    if (legacyMatch)
        return legacyMatch[1];

    if (/^\w+$/.test(trimmed))
        return trimmed;

    return null;
}

/**
 * @param {string} text
 * @param {string} methodName
 * @returns {boolean}
 */
export function scopeScriptHasMethod(text, methodName) {
    if (!text || !methodName)
        return false;

    const re = new RegExp(`\\b${escapeRegExp(methodName)}\\s*\\(`);
    return re.test(text);
}

/**
 * 1-based line number of the method signature, or null.
 * @param {string} text
 * @param {string} methodName
 * @returns {number|null}
 */
export function findMethodLineNumber(text, methodName) {
    if (!text || !methodName)
        return null;

    const lines = text.split('\n');
    const re = new RegExp(`\\b${escapeRegExp(methodName)}\\s*\\(`);

    for (let i = 0; i < lines.length; i++) {
        if (re.test(lines[i]))
            return i + 1;
    }

    return null;
}

function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const SCOPE_SCRIPT_METHOD_SHORTHAND = /^\s*(?:async\s+)?(\w+)\s*\(([^)]*)\)\s*\{/;
const SCOPE_SCRIPT_METHOD_CLASSIC = /^\s*(\w+)\s*:\s*(?:async\s+)?function\s*\(([^)]*)\)/;
const SCOPE_SCRIPT_METHOD_ARROW = /^\s*(\w+)\s*:\s*(?:async\s+)?\(([^)]*)\)\s*=>/;
const SCOPE_SCRIPT_VARIABLE = /^\s*(\w+)\s*:\s*(?!async\s+function\b)(?!function\s*\()/;
const JS_IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

function isMethodLine(line) {
    return SCOPE_SCRIPT_METHOD_SHORTHAND.test(line)
        || SCOPE_SCRIPT_METHOD_CLASSIC.test(line)
        || SCOPE_SCRIPT_METHOD_ARROW.test(line);
}

/**
 * @param {string} paramText
 * @returns {string|null}
 */
function parseSingleParameterName(paramText) {
    const param = String(paramText || '').trim();
    if (!param)
        return null;

    if (param.startsWith('...')) {
        const rest = param.slice(3).trim();
        const restMatch = /^([A-Za-z_$][\w$]*)/.exec(rest.split('=')[0].trim());
        return restMatch ? restMatch[1] : null;
    }

    if (param.startsWith('{') || param.startsWith('[')) {
        const destructMatch = /[A-Za-z_$][\w$]*/.exec(param);
        return destructMatch ? destructMatch[0] : null;
    }

    const beforeDefault = param.split('=')[0].trim();
    const match = /^([A-Za-z_$][\w$]*)/.exec(beforeDefault);
    return match ? match[1] : null;
}

/**
 * @param {string} paramListText
 * @returns {string[]}
 */
export function parseScopeScriptParameterList(paramListText) {
    if (!paramListText || !String(paramListText).trim())
        return [];

    return String(paramListText)
        .split(',')
        .map((part) => parseSingleParameterName(part))
        .filter((name) => !!name);
}

/**
 * Parse formal parameter names for a ScopeScript method member.
 * @param {string} text
 * @param {string} methodName
 * @returns {string[]|null} null when the method is not found
 */
export function parseScopeScriptMethodParameters(text, methodName) {
    if (!text || !methodName)
        return null;

    const lines = text.split('\n');

    for (const line of lines) {
        let match = SCOPE_SCRIPT_METHOD_SHORTHAND.exec(line);
        if (match && match[1] === methodName)
            return parseScopeScriptParameterList(match[2]);

        match = SCOPE_SCRIPT_METHOD_CLASSIC.exec(line);
        if (match && match[1] === methodName)
            return parseScopeScriptParameterList(match[2]);

        match = SCOPE_SCRIPT_METHOD_ARROW.exec(line);
        if (match && match[1] === methodName)
            return parseScopeScriptParameterList(match[2]);
    }

    return null;
}

/**
 * List function member names declared in a ScopeScript object-literal string.
 * @param {string} text
 * @returns {string[]}
 */
export function listScopeScriptMethods(text) {
    if (!text || typeof text !== 'string')
        return [];

    const names = new Set();
    const lines = text.split('\n');

    for (const line of lines) {
        let match = SCOPE_SCRIPT_METHOD_SHORTHAND.exec(line);
        if (match) {
            names.add(match[1]);
            continue;
        }

        match = SCOPE_SCRIPT_METHOD_CLASSIC.exec(line);
        if (match)
            names.add(match[1]);
    }

    return [...names].sort();
}

/**
 * List non-function property names declared in a ScopeScript object-literal string.
 * @param {string} text
 * @returns {string[]}
 */
export function listScopeScriptVariables(text) {
    if (!text || typeof text !== 'string')
        return [];

    const names = new Set();
    const lines = text.split('\n');

    for (const line of lines) {
        if (isMethodLine(line))
            continue;

        const match = SCOPE_SCRIPT_VARIABLE.exec(line);
        if (match)
            names.add(match[1]);
    }

    return [...names].sort();
}

/**
 * List all ScopeScript object members with kind (variable or function).
 * @param {string} text
 * @returns {{ name: string, kind: 'function' | 'variable' }[]}
 */
export function listScopeScriptMembers(text) {
    if (!text || typeof text !== 'string')
        return [];

    const byName = new Map();

    for (const name of listScopeScriptMethods(text))
        byName.set(name, { name, kind: 'function' });

    for (const name of listScopeScriptVariables(text)) {
        if (!byName.has(name))
            byName.set(name, { name, kind: 'variable' });
    }

    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * @param {string} name
 * @returns {boolean}
 */
export function isValidJsIdentifier(name) {
    return typeof name === 'string' && JS_IDENTIFIER.test(name);
}

/**
 * Sanitize a path segment into a valid JS identifier for scope include aliases.
 * @param {string} segment
 * @returns {string}
 */
export function sanitizeScopeIncludeAlias(segment) {
    if (!segment || typeof segment !== 'string')
        return '_lib';

    let alias = segment.replace(/[^A-Za-z0-9_$]/g, '');
    if (!alias)
        alias = '_lib';
    if (/^[0-9]/.test(alias))
        alias = `_${alias}`;

    return alias;
}

/**
 * Suggest a unique As alias for a scope include path.
 * @param {string} path
 * @param {Iterable<string>} usedAliases
 * @returns {string}
 */
export function suggestScopeIncludeAlias(path, usedAliases = []) {
    const used = new Set(usedAliases);
    const normalized = String(path || '').replace(/^\/+|\/+$/g, '');
    const segments = normalized.split('/').filter(Boolean);
    const base = sanitizeScopeIncludeAlias(segments[segments.length - 1] || normalized || 'lib');

    if (!used.has(base))
        return base;

    let index = 2;
    while (used.has(`${base}${index}`))
        index += 1;

    return `${base}${index}`;
}

/**
 * Format selected labels for compact dropdown display.
 * @param {string[]} labels
 * @param {{ maxItems?: number, maxChars?: number }} [options]
 * @returns {string}
 */
export function formatTruncatedSelection(labels, options = {}) {
    const items = (labels || []).filter((label) => label != null && String(label).trim() !== '');
    if (items.length === 0)
        return '';

    const maxItems = Number.isFinite(options.maxItems) ? options.maxItems : 2;
    const maxChars = Number.isFinite(options.maxChars) ? options.maxChars : 40;

    const visible = items.slice(0, Math.max(1, maxItems));
    let text = visible.join(', ');
    const hasMore = items.length > visible.length;

    if (hasMore)
        text = `${text}, ...`;
    else if (text.length > maxChars)
        text = `${text.slice(0, Math.max(0, maxChars - 4)).trimEnd()}, ...`;

    return text;
}

/**
 * Find the insertion point before the closing `})` of the outer object literal.
 * @param {string} text
 * @returns {number|null} index to insert before
 */
export function findScopeScriptInsertIndex(text) {
    if (!text)
        return null;

    const trimmed = text.trimEnd();
    const closeMatch = /\}\s*\)\s*$/.exec(trimmed);
    if (!closeMatch)
        return null;

    return closeMatch.index;
}

/**
 * Append a method member block before the closing `})`.
 * @param {string} text
 * @param {string} methodSourceBlock - member source without trailing comma
 * @returns {string}
 */
export function appendMethodToScopeScript(text, methodSourceBlock) {
    if (!methodSourceBlock)
        return text || '';

    const source = text || '({\n})';
    const insertAt = findScopeScriptInsertIndex(source);
    if (insertAt == null)
        return source;

    const before = source.slice(0, insertAt).trimEnd();
    const after = source.slice(insertAt);

    const inner = before.slice(before.lastIndexOf('{') + 1).trim();
    const needsComma = inner.length > 0 && !before.endsWith(',');
    const prefix = needsComma ? ',\n' : (inner.length > 0 ? '\n' : '\n');

    return `${before}${prefix}${methodSourceBlock}\n${after}`;
}

/**
 * @param {object} action
 * @returns {boolean}
 */
export function isDefaultScriptAction(action) {
    if (!action || typeof action !== 'object')
        return false;

    if (action.ActionType === 'Script')
        return true;

    const schema = action.$typeSchema;
    return typeof schema === 'string' && (schema.endsWith('/UIAction_Script') || schema.includes('/UIAction_Script'));
}
